import { Injectable } from '@angular/core';

/**
 * Holds the IndexedDB encryption key (Issue #4).
 *
 * The key is a 32-byte secret issued by the backend at login and kept
 * exclusively in memory: it is never written to `localStorage`,
 * `sessionStorage`, IndexedDB or any other persistent store. It is wiped on
 * logout / user change. When the browser tab is closed the key is lost and the
 * user must sign in online again to decrypt the local database — this is the
 * intended posture for a lost/shared device.
 *
 * This service deliberately exposes no persistence API.
 */
@Injectable({ providedIn: 'root' })
export class DbKeyService {
    private static readonly KEY_LENGTH = 32;

    private key: Uint8Array | null = null;

    /**
     * Stores the key from its base64 transport representation.
     * @throws if the decoded key is not exactly 32 bytes.
     */
    setKey(base64Key: string): void {
        const bytes = DbKeyService.decodeBase64(base64Key);

        if (bytes.length !== DbKeyService.KEY_LENGTH) {
            throw new Error(
                `DbKeyService: expected a ${DbKeyService.KEY_LENGTH}-byte key, got ${bytes.length}.`
            );
        }

        // Wipe any previous key before replacing it.
        this.clear();
        this.key = bytes;
    }

    /**
     * Returns the raw key.
     * @throws if no key is currently held.
     */
    getKey(): Uint8Array {
        if (!this.key) {
            throw new Error('DbKeyService: no encryption key is available.');
        }

        return this.key;
    }

    hasKey(): boolean {
        return this.key !== null;
    }

    /** Zero-fills and releases the key. Safe to call when no key is held. */
    clear(): void {
        if (this.key) {
            this.key.fill(0);
        }

        this.key = null;
    }

    private static decodeBase64(base64: string): Uint8Array {
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);

        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }

        return bytes;
    }
}
